import { useState, useCallback, useEffect } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import TrackGuide from './TrackGuide';

const SESSION_ICON = { Race:'🏁', Qualifying:'⏱', Practice:'🔧', Sprint:'⚡', 'Sprint Qualifying':'⚡' };

// Accurate F1 circuit outlines — traced from official circuit maps
const TRACK_SHAPES = {
  // Bahrain — twin hairpins, stadium section
  'Sakhir': 'M72,18 L82,18 L88,24 L88,35 L82,40 L72,42 L65,45 L65,52 L72,55 L82,55 L88,61 L88,76 L82,82 L18,82 L12,76 L12,65 L18,60 L28,58 L35,55 L35,48 L28,45 L18,42 L12,36 L12,24 L18,18 L72,18 Z',
  // Jeddah — long straights, flowing walls
  'Jeddah': 'M50,8 L75,10 L84,16 L88,26 L86,48 L80,58 L74,64 L68,68 L65,76 L68,82 L72,86 L68,90 L50,92 L32,90 L28,86 L32,82 L35,76 L32,68 L26,64 L20,58 L14,48 L12,26 L16,16 L25,10 L50,8 Z',
  // Albert Park — anti-clockwise lake circuit
  'Melbourne': 'M55,12 L70,14 L80,20 L86,30 L88,42 L84,54 L80,62 L76,68 L72,76 L68,82 L58,88 L48,90 L38,88 L30,84 L24,78 L18,70 L14,60 L12,48 L14,36 L20,26 L28,18 L38,13 L55,12 Z M44,42 L54,40 L60,46 L58,54 L50,57 L42,54 L38,47 Z',
  // Shanghai — hairpin complex, long pit straight
  'Shanghai': 'M15,30 L15,20 L22,14 L40,12 L55,14 L65,20 L72,28 L75,38 L72,48 L65,55 L75,60 L82,66 L86,74 L84,82 L78,88 L35,88 L28,84 L24,78 L26,70 L32,64 L25,58 L18,50 L15,40 L15,30 Z',
  // Suzuka — iconic figure-of-8 with overpass
  'Suzuka': 'M50,8 L60,10 L68,16 L72,24 L70,34 L64,40 L56,42 L56,50 L64,54 L72,62 L76,72 L74,82 L68,88 L58,90 L42,90 L32,88 L26,82 L24,72 L28,62 L36,54 L44,50 L44,42 L36,40 L30,34 L28,24 L32,16 L40,10 L50,8 Z M47,44 L50,47 L53,44 L50,41 Z',
  // Miami — purpose-built, Hard Rock oval infield
  'Miami': 'M20,25 L75,22 L84,28 L88,38 L86,52 L80,62 L72,70 L68,78 L72,84 L68,90 L35,90 L28,86 L24,80 L20,72 L16,60 L14,46 L16,34 L20,25 Z',
  // Imola — hillside twisty circuit, Tamburello
  'Imola': 'M52,10 L64,12 L74,18 L80,28 L84,40 L82,54 L76,65 L68,73 L60,80 L56,88 L44,90 L40,84 L38,76 L30,70 L22,62 L18,52 L16,38 L20,26 L28,17 L40,12 L52,10 Z',
  // Monaco — tight, famous hairpin and tunnel
  'Monaco': 'M62,12 L74,14 L82,20 L86,30 L84,40 L76,46 L64,48 L56,52 L58,60 L64,66 L66,74 L62,82 L54,87 L44,88 L34,86 L26,80 L22,72 L24,62 L30,54 L36,46 L28,40 L24,32 L26,22 L34,16 L48,12 L62,12 Z',
  // Barcelona — complex infield, long pit straight
  'Barcelona': 'M18,28 L18,18 L26,12 L55,10 L70,12 L80,20 L86,30 L88,44 L84,56 L76,64 L68,70 L62,76 L62,85 L54,90 L40,90 L32,86 L26,80 L24,72 L28,64 L36,58 L28,50 L20,42 L18,28 Z',
  // Montreal — island circuit, chicanes, wall of champions
  'Montreal': 'M25,15 L72,12 L82,18 L88,28 L88,62 L84,72 L76,80 L64,85 L55,87 L55,91 L45,91 L45,87 L36,85 L24,80 L16,72 L12,62 L12,28 L18,18 L25,15 Z',
  // Red Bull Ring — short, uphill turn 1
  'Spielberg': 'M50,10 L66,12 L76,20 L82,32 L82,52 L76,62 L68,70 L58,76 L50,80 L42,76 L32,70 L24,62 L18,52 L18,32 L24,20 L34,12 L50,10 Z',
  // Silverstone — fast, Maggotts-Becketts-Chapel
  'Silverstone': 'M28,15 L55,10 L72,12 L82,20 L88,34 L88,52 L84,64 L76,74 L64,82 L52,88 L38,88 L26,82 L16,72 L12,58 L12,38 L18,24 L28,15 Z',
  // Spa — Eau Rouge, Raidillon, Pouhon, long Kemmel straight
  'Spa': 'M45,10 L62,10 L74,16 L82,26 L88,40 L86,56 L80,67 L70,76 L60,82 L48,86 L36,84 L26,78 L17,67 L13,53 L13,38 L18,25 L28,16 L45,10 Z M36,44 L46,40 L54,44 L56,54 L50,59 L40,57 L34,51 Z',
  // Hungaroring — tight and twisty, 14 corners
  'Budapest': 'M22,18 L76,15 L84,22 L88,34 L86,52 L82,65 L72,74 L60,81 L48,85 L36,83 L24,77 L15,65 L12,52 L14,38 L18,26 L22,18 Z',
  // Zandvoort — banked corner, beachside
  'Zandvoort': 'M50,10 L65,12 L76,20 L82,32 L82,54 L78,65 L70,74 L58,82 L46,86 L34,84 L24,77 L17,66 L14,53 L15,37 L22,23 L32,14 L50,10 Z',
  // Monza — Temple of Speed, two chicanes, oval banking
  'Monza': 'M20,15 L80,12 L88,20 L88,44 L80,50 L66,52 L66,62 L80,64 L88,70 L88,82 L82,88 L20,88 L12,82 L12,70 L20,64 L34,62 L34,52 L20,50 L12,44 L12,20 L20,15 Z',
  // Baku — castle section, narrow old city streets
  'Baku': 'M50,8 L72,10 L82,18 L88,32 L86,58 L80,68 L70,74 L62,80 L60,86 L54,90 L46,90 L40,86 L38,80 L30,74 L20,68 L14,58 L12,32 L18,18 L28,10 L50,8 Z',
  // Marina Bay — night race, 23 turns through the city
  'Marina Bay': 'M45,10 L66,10 L78,18 L84,28 L86,44 L82,58 L74,68 L68,76 L70,84 L65,90 L35,90 L30,84 L32,76 L26,68 L18,58 L14,44 L16,28 L22,18 L35,10 L45,10 Z',
  // COTA — uphill turn 1, flowing infield, long back straight
  'Austin': 'M22,18 L22,10 L30,8 L56,8 L73,12 L83,23 L87,37 L85,54 L79,66 L69,75 L59,80 L59,88 L49,91 L41,88 L41,80 L29,75 L19,63 L13,50 L13,35 L17,24 L22,18 Z',
  // Hermanos Rodriguez — stadium section, peraltada remnant
  'Mexico City': 'M18,20 L80,16 L87,24 L89,38 L89,56 L85,67 L75,76 L61,82 L52,86 L52,91 L42,91 L42,86 L33,82 L19,76 L11,67 L9,56 L9,38 L11,26 L18,20 Z',
  // Interlagos — anti-clockwise, Senna S, Juncao
  'Interlagos': 'M55,10 L70,12 L80,20 L86,32 L84,48 L76,60 L66,68 L58,74 L55,82 L50,88 L40,88 L34,82 L32,74 L24,68 L14,60 L12,46 L13,32 L19,20 L29,13 L55,10 Z',
  // Las Vegas — three long straights, casino strip
  'Las Vegas': 'M18,80 L14,22 L22,12 L78,10 L87,18 L89,34 L89,52 L80,60 L66,64 L66,80 L60,89 L38,89 L30,83 L25,72 L20,68 L18,80 Z',
  // Losail — sweeping corners, floodlit
  'Lusail': 'M50,10 L66,12 L78,20 L86,34 L86,58 L80,72 L68,82 L54,88 L46,88 L30,82 L18,72 L14,58 L14,34 L22,20 L34,12 L50,10 Z',
  // Yas Marina — hotel section, final chicane
  'Yas Marina': 'M20,50 L15,25 L22,14 L38,10 L63,10 L79,15 L86,26 L87,44 L83,59 L73,69 L63,75 L65,83 L60,91 L40,91 L35,83 L37,75 L27,69 L19,59 L20,50 Z',

  'default': 'M50,10 Q85,10 90,45 Q90,80 50,88 Q10,80 10,45 Q15,10 50,10 Z',
};

function TrackSVG({ circuitName, colour='#e8002d', size=100 }) {
  const path = TRACK_SHAPES[circuitName] || TRACK_SHAPES.default;
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} style={{display:'block'}}>
      <path d={path} fill="none" stroke={colour} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" opacity="0.7"/>
      <path d={path} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// Build F1 CDN circuit image URL from circuit name
// Pattern: https://media.formula1.com/image/upload/f_auto,c_limit,q_auto,w_771/content/dam/fom-website/2018-redesign-assets/Track%20icons%204x3/{Name}%20carbon
function f1CircuitImageUrl(circuitName) {
  if (!circuitName) return null;
  // Map circuit short names to F1 CDN names
  const CDN_MAP = {
    'Sakhir':      'Bahrain',
    'Jeddah':      'Saudi Arabia',
    'Melbourne':   'Australia',
    'Shanghai':    'China',
    'Suzuka':      'Japan',
    'Miami':       'Miami',
    'Imola':       'Emilia Romagna',
    'Monaco':      'Monaco',
    'Barcelona':   'Spain',
    'Montreal':    'Canada',
    'Spielberg':   'Austria',
    'Silverstone': 'Great Britain',
    'Budapest':    'Hungary',
    'Spa':         'Belgium',
    'Zandvoort':   'Netherlands',
    'Monza':       'Italy',
    'Baku':        'Azerbaijan',
    'Marina Bay':  'Singapore',
    'Austin':      'USA',
    'Mexico City': 'Mexico',
    'Interlagos':  'Brazil',
    'Las Vegas':   'Las Vegas',
    'Lusail':      'Qatar',
    'Yas Marina':  'Abu Dhabi',
  };
  const name = CDN_MAP[circuitName] || circuitName;
  return `https://media.formula1.com/image/upload/f_auto,c_limit,q_auto,w_771/content/dam/fom-website/2018-redesign-assets/Track%20icons%204x3/${encodeURIComponent(name)}%20carbon`;
}

function TrackImage({ circuitName, circuitImageUrl, size=100, style={} }) {
  const [failed, setFailed] = useState(false);
  // Prefer the URL from OpenF1 meetings data, fall back to CDN pattern, fall back to SVG
  const imgUrl = !failed ? (circuitImageUrl || f1CircuitImageUrl(circuitName)) : null;
  if (imgUrl) {
    return (
      <img
        src={imgUrl}
        alt={circuitName}
        onError={() => setFailed(true)}
        style={{ width:size, height:size, objectFit:'contain', display:'block', ...style }}
      />
    );
  }
  return <TrackSVG circuitName={circuitName} size={size}/>;
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function F1Tracks({ onBack }) {
  const [sessions, setSessions]   = useState([]);
  const [season, setSeason]       = useState(2026);
  const [monthFilter, setMonthFilter] = useState(null);
  const [selected, setSelected]       = useState(null); // selected meeting
  const [showGuide, setShowGuide]       = useState(false);
  const [activeSession, setActiveSession] = useState(null); // selected session key
  const [sessionSnap, setSessionSnap]     = useState(null); // loaded session data
  const [sessionLoading, setSessionLoading] = useState(false);

  const onMsg = useCallback((msg) => {
    if (msg.type === 'F1_SESSIONS') setSessions(msg.sessions);
    if (msg.type === 'F1_SNAPSHOT') {
      if (msg.snapshot?.sessionList?.length > 0) setSessions(msg.snapshot.sessionList);
      // If we're loading a specific session, capture the snap
      setSessionSnap(msg.snapshot);
      setSessionLoading(false);
    }
  }, []);
  const { send } = useWebSocket(onMsg);
  useEffect(() => { send('F1_GET_SESSIONS'); send('F1_GET_SNAPSHOT'); }, [send]);

  const loadSession = (sessionKey) => {
    setActiveSession(sessionKey);
    setSessionLoading(true);
    setSessionSnap(null);
    send('F1_LOAD_SESSION', { sessionKey });
  };

  // Group sessions by meeting
  const meetingMap = {};
  sessions.filter(s => !s.isCancelled).forEach(s => {
    const key = s.meetingKey || s.meetingName;
    if (!meetingMap[key]) meetingMap[key] = { key, name:s.meetingName, country:s.countryName, circuitName:s.circuitName, sessions:[] };
    meetingMap[key].sessions.push(s);
  });
  const meetings = Object.values(meetingMap)
    .map(m => ({ ...m, sessions: [...m.sessions].sort((a,b)=>new Date(a.dateStart)-new Date(b.dateStart)) }))
    .sort((a,b) => new Date(a.sessions[0].dateStart) - new Date(b.sessions[0].dateStart));

  // Filter by month
  const filtered = meetings.filter(m => {
    if (!monthFilter) return true;
    const raceDate = new Date(m.sessions[m.sessions.length-1].dateStart);
    return raceDate.getMonth() === monthFilter;
  });

  // Months available in data
  const availableMonths = [...new Set(meetings.map(m => new Date(m.sessions[m.sessions.length-1].dateStart).getMonth()))].sort();

  const now = Date.now();

  if (selected && showGuide) {
    return <TrackGuide circuitName={selected.circuitName} onBack={()=>setShowGuide(false)}/>;
  }

  if (selected) {
    const m      = selected;
    const raceS  = m.sessions.find(s=>s.sessionType==='Race') || m.sessions[m.sessions.length-1];
    const raceDur= { Race:2*3600*1000, Qualifying:75*60*1000, Practice:90*60*1000, Sprint:60*60*1000 };
    const raceEnd= new Date(raceS.dateStart).getTime() + (raceDur[raceS.sessionType]||2*3600*1000);
    const isPast = now > raceEnd;
    const isLive = now >= new Date(raceS.dateStart).getTime() && !isPast;

    const lb      = sessionSnap?.leaderboard || [];
    const weather = sessionSnap?.weather;
    const rcMsgs  = sessionSnap?.rcMessages || [];
    const activeS = m.sessions.find(s => s.sessionKey === activeSession);

    return (
      <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
        <div className="g-hdr f1-hdr">
          <button className="g-back" onClick={()=>{ setSelected(null); setActiveSession(null); setSessionSnap(null); }}>← Tracks</button>
          <div className="g-title" style={{color:'#e8002d'}}>{m.country?.toUpperCase()}</div>
          {activeS && <div style={{fontSize:'0.6rem',color:'#e8002d'}}>{activeS.sessionName}</div>}
        </div>
        <div className="f1-live-wrap">

          {/* Hero */}
          <div style={{background:'var(--surface,#0f1e38)',border:'1px solid var(--border,#1e3a6e)',borderRadius:14,padding:'16px',display:'flex',alignItems:'center',gap:16}}>
            <div style={{flexShrink:0}}><TrackImage circuitName={m.circuitName} circuitImageUrl={m.sessions[0]?.circuitImage} size={120}/></div>
            <div>
              <div style={{fontSize:'0.55rem',letterSpacing:'0.18em',color:'var(--sub,#4a6a9e)',textTransform:'uppercase',marginBottom:3}}>2026 Formula 1</div>
              <div style={{fontSize:'1.1rem',fontWeight:700,color:'var(--text,#e8edf8)',lineHeight:1.2}}>{m.name}</div>
              <div style={{fontSize:'0.72rem',color:'var(--sub,#8ba3cc)',marginTop:3}}>{m.circuitName} · {m.country}</div>
              <div style={{fontSize:'0.62rem',color:'var(--sub,#4a6a9e)',marginTop:4}}>
                {new Date(raceS.dateStart).toLocaleDateString([],{weekday:'short',day:'numeric',month:'long',year:'numeric'})}
              </div>
              <div style={{display:'flex',gap:6,marginTop:6,flexWrap:'wrap',alignItems:'center'}}>
                <div style={{display:'inline-flex',padding:'2px 8px',borderRadius:4,fontSize:'0.52rem',fontWeight:700,letterSpacing:'0.1em',
                  border:'1px solid '+(isLive?'#e8002d':isPast?'var(--border,#1e3a6e)':'#0369a1'),
                  color:isLive?'#e8002d':isPast?'var(--sub,#3a5a8e)':'#38bdf8'}}>
                  {isLive?'● LIVE WEEKEND':isPast?'COMPLETED':'UPCOMING'}
                </div>
                <button onClick={()=>setShowGuide(true)}
                  style={{display:'inline-flex',padding:'2px 8px',borderRadius:4,fontSize:'0.52rem',
                    fontWeight:700,letterSpacing:'0.1em',border:'1px solid #e8002d',color:'#e8002d',
                    background:'rgba(232,0,45,0.08)',cursor:'pointer',fontFamily:'Inter,sans-serif'}}>
                  🏎️ Track Guide
                </button>
              </div>
            </div>
          </div>

          {/* Session selector */}
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            <div style={{fontSize:'0.55rem',letterSpacing:'0.18em',color:'var(--sub,#4a6a9e)',textTransform:'uppercase'}}>
              Tap a session to load timing data
            </div>
            {m.sessions.map(s => {
              const sStart   = new Date(s.dateStart).getTime();
              const sEnd     = s.dateEnd ? new Date(s.dateEnd).getTime() : sStart+(raceDur[s.sessionType]||90*60*1000);
              const sLive    = now>=sStart && now<=sEnd;
              const sPast    = now>sEnd;
              const isActive = activeSession === s.sessionKey;
              const bCol     = isActive?'#e8002d':sLive?'#e8002d':'var(--border,#1e3a6e)';
              return (
                <button key={s.sessionKey}
                  onClick={() => loadSession(s.sessionKey)}
                  style={{display:'flex',alignItems:'center',gap:12,padding:'12px 14px',
                    borderRadius:10,background:isActive?'rgba(232,0,45,0.06)':'var(--surface,#0f1e38)',
                    border:`1px solid ${bCol}`,cursor:'pointer',width:'100%',
                    textAlign:'left',fontFamily:'Inter,sans-serif',transition:'border-color 0.15s,background 0.15s'}}>
                  <span style={{fontSize:'1.1rem',flexShrink:0}}>{SESSION_ICON[s.sessionType]||'🏎️'}</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:'0.84rem',fontWeight:700,color:isActive?'#e8002d':sLive?'#e8002d':'var(--text,#e8edf8)'}}>
                      {s.sessionName}
                    </div>
                    <div style={{fontSize:'0.62rem',color:'var(--sub,#4a6a9e)',marginTop:2}}>
                      {new Date(s.dateStart).toLocaleString([],{weekday:'short',day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}
                    </div>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:2}}>
                    {sLive  && <span style={{fontSize:'0.5rem',color:'#e8002d',fontWeight:700,animation:'pulse 1.5s ease infinite'}}>● LIVE</span>}
                    {!sLive && !sPast && <span style={{fontSize:'0.5rem',color:'#38bdf8'}}>UPCOMING</span>}
                    {sPast  && !sLive && <span style={{fontSize:'0.5rem',color:'var(--sub,#3a5a8e)'}}>COMPLETED</span>}
                    {!isActive && <span style={{fontSize:'0.6rem',color:'var(--sub,#3a5a8e)'}}>›</span>}
                    {isActive && sessionLoading && <span style={{fontSize:'0.5rem',color:'#e8002d'}}>Loading...</span>}
                    {isActive && !sessionLoading && <span style={{fontSize:'0.5rem',color:'#e8002d'}}>● Loaded</span>}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Session data panel — shown when a session is selected */}
          {activeSession && (
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {/* Active session header */}
              {activeS && (
                <div style={{padding:'10px 14px',borderRadius:10,background:'rgba(232,0,45,0.06)',
                  border:'1px solid #e8002d',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                  <div>
                    <div style={{fontSize:'0.52rem',letterSpacing:'0.15em',color:'#e8002d',textTransform:'uppercase'}}>{m.meetingName || m.name}</div>
                    <div style={{fontSize:'0.9rem',fontWeight:700,color:'var(--text,#e8edf8)',marginTop:2}}>{activeS.sessionName}</div>
                  </div>
                  <button onClick={()=>{setActiveSession(null);setSessionSnap(null);}}
                    style={{background:'transparent',border:'1px solid var(--border,#1e3a6e)',borderRadius:6,
                      color:'var(--sub,#4a6a9e)',padding:'3px 8px',cursor:'pointer',fontSize:'0.65rem',fontFamily:'Inter,sans-serif'}}>
                    Clear
                  </button>
                </div>
              )}

              {sessionLoading && (
                <p style={{color:'var(--sub,#8ba3cc)',textAlign:'center',fontSize:'0.8rem',padding:16}}>
                  Loading session data...
                </p>
              )}

              {!sessionLoading && sessionSnap && <>
                {/* Weather */}
                {weather && (
                  <div className="f1-weather">
                    {[
                      { val: weather.airTemp?.toFixed(0)+'°',   lbl:'Air' },
                      { val: weather.trackTemp?.toFixed(0)+'°', lbl:'Track' },
                      { val: weather.humidity?.toFixed(0)+'%',  lbl:'Humidity' },
                      { val: weather.windSpeed?.toFixed(1),      lbl:'Wind m/s' },
                      { val: weather.rainfall>0?'🌧️':'☀️',      lbl:weather.rainfall>0?'Rain':'Dry' },
                    ].map(({val,lbl}) => (
                      <div key={lbl} className="f1-weather-item">
                        <span className="f1-weather-val">{val||'—'}</span>
                        <span className="f1-weather-lbl">{lbl}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Race control */}
                {rcMsgs.length > 0 && (
                  <div>
                    <div style={{fontSize:'0.55rem',letterSpacing:'0.18em',color:'var(--sub,#4a6a9e)',textTransform:'uppercase',marginBottom:5}}>Race Control</div>
                    <div className="f1-rc">
                      {rcMsgs.slice(0,5).map((msg,i) => {
                        const m2 = (msg.message||'').toUpperCase();
                        const cls = m2.includes('VIRTUAL SAFETY CAR')?'vsc':m2.includes('SAFETY CAR')?'sc':m2.includes('RED FLAG')||m2.includes('CHEQUERED')?'flag':m2.includes('PENALTY')?'penalty':'';
                        return (
                          <div key={i} className={'f1-rc-msg '+cls}>
                            <span className="f1-rc-time">L{msg.lap||'—'}</span>
                            <span style={{fontSize:'0.72rem'}}>{msg.message}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Leaderboard */}
                {lb.length > 0 ? (
                  <div className="f1-leaderboard">
                    <div className="f1-lb-hdr">
                      <span style={{width:26}}>P</span>
                      <span style={{width:4}}/>
                      <span style={{flex:1}}>Driver</span>
                      <span style={{minWidth:80}}>Team</span>
                    </div>
                    {lb.map((d,i) => (
                      <div key={d.driverNumber||i} className="f1-lb-row">
                        <span className={'f1-pos '+(i===0?'p1':i===1?'p2':i===2?'p3':'other')}>{d.position}</span>
                        <div className="f1-team-bar" style={{background:d.teamColour||'#3a5a8e'}}/>
                        <div className="f1-driver" style={{flex:1}}>
                          <span className="f1-driver-name">{d.acronym}</span>
                          <span className="f1-driver-team">{d.fullName}</span>
                        </div>
                        <span style={{fontSize:'0.65rem',color:'var(--sub,#4a6a9e)',minWidth:80}}>
                          {d.teamName?.split(' ').slice(-1)[0]}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{textAlign:'center',padding:16}}>
                    <p style={{color:'var(--sub,#8ba3cc)',fontSize:'0.8rem'}}>No position data for this session</p>
                    <p style={{color:'var(--sub,#4a6a9e)',fontSize:'0.7rem',marginTop:4}}>
                      Position data is available for completed sessions and during live sessions
                    </p>
                  </div>
                )}
              </>}
            </div>
          )}

        </div>
      </div>
    );
  }

  return (
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr f1-hdr">
        <button className="g-back" onClick={onBack}>← Back</button>
        <div className="g-title" style={{color:'#e8002d'}}>2026 TRACKS</div>
        <div style={{fontSize:'0.6rem',color:'var(--sub,#4a6a9e)'}}>{filtered.length} races</div>
      </div>

      <div className="f1-live-wrap">
        {/* Month filter */}
        <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
          <button onClick={()=>setMonthFilter(null)}
            style={{padding:'4px 10px',borderRadius:6,border:`1px solid ${monthFilter===null?'#e8002d':'var(--border,#1e3a6e)'}`,
              background:monthFilter===null?'rgba(232,0,45,0.1)':'transparent',
              color:monthFilter===null?'#e8002d':'var(--sub,#4a6a9e)',cursor:'pointer',fontSize:'0.68rem',fontFamily:'Inter,sans-serif'}}>
            All
          </button>
          {availableMonths.map(mo => (
            <button key={mo} onClick={()=>setMonthFilter(monthFilter===mo?null:mo)}
              style={{padding:'4px 10px',borderRadius:6,border:`1px solid ${monthFilter===mo?'#e8002d':'var(--border,#1e3a6e)'}`,
                background:monthFilter===mo?'rgba(232,0,45,0.1)':'transparent',
                color:monthFilter===mo?'#e8002d':'var(--sub,#4a6a9e)',cursor:'pointer',fontSize:'0.68rem',fontFamily:'Inter,sans-serif'}}>
              {MONTHS[mo]}
            </button>
          ))}
        </div>

        {/* Track grid */}
        {sessions.length === 0 && <p style={{color:'var(--sub,#4a6a9e)',textAlign:'center',fontSize:'0.8rem'}}>Loading tracks...</p>}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          {filtered.map(m => {
            const raceS   = m.sessions.find(s=>s.sessionType==='Race') || m.sessions[m.sessions.length-1];
            const raceTime = new Date(raceS.dateStart).getTime();
            const isPast  = now > raceTime + 3*3600*1000;
            const isLive  = now >= raceTime && !isPast;
            const isUp    = !isPast && !isLive;
            const bCol    = isLive?'#e8002d':isUp?'#0369a1':'var(--border,#1e3a6e)';
            return (
              <button key={m.key} onClick={()=>{ setSelected(m); setActiveSession(null); setSessionSnap(null); }}
                style={{padding:'14px 12px',borderRadius:12,border:`1px solid ${bCol}`,
                  background:'var(--surface,#0f1e38)',cursor:'pointer',textAlign:'left',
                  display:'flex',flexDirection:'column',alignItems:'center',gap:8,
                  opacity:isPast?0.7:1,transition:'border-color 0.15s,transform 0.12s'}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor='#e8002d';e.currentTarget.style.transform='translateY(-2px)';}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=bCol;e.currentTarget.style.transform='none';}}>
                <TrackImage circuitName={m.circuitName} circuitImageUrl={m.sessions[0]?.circuitImage} size={80} style={{filter:isPast?'grayscale(0.6)':'none'}}/>
                <div style={{textAlign:'center',width:'100%'}}>
                  <div style={{fontSize:'0.78rem',fontWeight:700,color:'var(--text,#e8edf8)',lineHeight:1.2}}>{m.country}</div>
                  <div style={{fontSize:'0.6rem',color:'var(--sub,#4a6a9e)',marginTop:2,lineHeight:1.3}}>{m.name?.replace(' Grand Prix','').replace(' GP','')}</div>
                  <div style={{fontSize:'0.58rem',color:'var(--sub,#3a5a8e)',marginTop:3}}>
                    {new Date(raceS.dateStart).toLocaleDateString([],{day:'numeric',month:'short'})}
                  </div>
                  {isLive&&<div style={{fontSize:'0.5rem',color:'#e8002d',fontWeight:700,marginTop:3,animation:'pulse 1.5s ease infinite'}}>● LIVE</div>}
                  {isUp&&<div style={{fontSize:'0.5rem',color:'#38bdf8',marginTop:3}}>UPCOMING</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
